import type { $Enums } from '@prisma/client';
import { z } from 'zod';

import type { Expect, Mirrors } from './mirrors';

// Mirrors of enums in `apps/work-permit/prisma/schema.prisma`. A value added
// there and not here fails the parity check at the bottom; see docs/adr/0013.

export const AccessScopeSchema = z.enum([
  'ORG',
  'SYSTEM',
  'WORKFLOW',
  'WORKFLOW_TYPE',
  'SITE_WORKFLOW_TYPE',
  'ORG_WORKFLOW_TYPE',
  'TENANT',
]);

export const ApprovalTypeSchema = z.enum(['H1', 'H2', 'H3', 'H4', 'C1']);

export const JSARiskLevelSchema = z.enum([
  'NEGLIGIBLE_VERY_UNLIKELY',
  'NEGLIGIBLE_UNLIKELY',
  'NEGLIGIBLE_POSSIBLE',
  'NEGLIGIBLE_LIKELY',
  'NEGLIGIBLE_VERY_LIKELY',
  'MINOR_VERY_UNLIKELY',
  'MINOR_UNLIKELY',
  'MINOR_POSSIBLE',
  'MINOR_LIKELY',
  'MINOR_VERY_LIKELY',
  'MODERATE_VERY_UNLIKELY',
  'MODERATE_UNLIKELY',
  'MODERATE_POSSIBLE',
  'MODERATE_LIKELY',
  'MODERATE_VERY_LIKELY',
  'SIGNIFICANT_VERY_UNLIKELY',
  'SIGNIFICANT_UNLIKELY',
  'SIGNIFICANT_POSSIBLE',
  'SIGNIFICANT_LIKELY',
  'SIGNIFICANT_VERY_LIKELY',
  'SEVERE_VERY_UNLIKELY',
  'SEVERE_UNLIKELY',
  'SEVERE_POSSIBLE',
  'SEVERE_LIKELY',
  'SEVERE_VERY_LIKELY',
]);

export type JSARiskLevelType = z.infer<typeof JSARiskLevelSchema>;

export const LikelihoodLevelSchema = z.enum([
  'VERY_LIKELY',
  'LIKELY',
  'POSSIBLE',
  'UNLIKELY',
  'VERY_UNLIKELY',
]);

export const LogTypeSchema = z.enum([
  'UNKNOWN',
  'USER_LOGIN',
  'USER_LOGOUT',
  'WORKFLOW_CREATED',
  'WORKFLOW_TASK_ASSIGNED',
  'WORKFLOW_STATE_CHANGED',
  'WORKFLOW_ACTION_PERFORMED',
  'WORKFLOW_FILE_UPLOADED',
  'WORKFLOW_FILE_REMOVED',
]);

export const RolesSchema = z.enum([
  'ISSUER',
  'RECEIVER',
  'GAS_TESTER',
  'DIVISION_MANAGER',
  'INITIATOR',
  'PRE_WORK_GAS_TESTER',
  'WORK_ON_SITE_GAS_TESTER',
  'POST_WORK_GAS_TESTER',
  'COUNTER_SIGNEE',
  'JSA_LEAD',
  'JSA_TEAM_MEMBER',
  'JSA_FOREMAN',
  'JSA_APPROVER',
  'JSA_REQUESTER',
  'DESIGNATED_REPRESENTATIVE',
  'IP_REQUESTER',
  'IP_PROCESS_ENGINEER',
  'IP_TEAM_LEAD',
  'IP_FIELD_VERIFICATION',
  'IP_TEAM_MEMBER',
  'IP_REVIEWER',
  'AUDIT_LEAD',
  'AUDIT_TEAM',
]);

export const SeverityLevelSchema = z.enum([
  'NEGLIGIBLE',
  'MINOR',
  'MODERATE',
  'SIGNIFICANT',
  'SEVERE',
]);

export const WorkflowInstanceRelationSchema = z.enum([
  'AUDITED_PERMIT',
  'SELECTED_ISOLATION_PLAN',
  'ORIGINATING_ISOLATION_REQUEST',
  'SPAWNING_ISOLATION_REQUEST',
  'EXECUTED_ISOLATION_PLAN',
]);

export type WorkflowInstanceRelationType = z.infer<
  typeof WorkflowInstanceRelationSchema
>;

// Compile-time parity with `schema.prisma`; see `./mirrors`.
export type EnumsMirrorPrisma = [
  Expect<Mirrors<z.infer<typeof AccessScopeSchema>, $Enums.AccessScope>>,
  Expect<Mirrors<z.infer<typeof ApprovalTypeSchema>, $Enums.ApprovalType>>,
  Expect<Mirrors<JSARiskLevelType, $Enums.JSARiskLevel>>,
  Expect<
    Mirrors<z.infer<typeof LikelihoodLevelSchema>, $Enums.LikelihoodLevel>
  >,
  Expect<Mirrors<z.infer<typeof LogTypeSchema>, $Enums.LogType>>,
  Expect<Mirrors<z.infer<typeof RolesSchema>, $Enums.Roles>>,
  Expect<Mirrors<z.infer<typeof SeverityLevelSchema>, $Enums.SeverityLevel>>,
  Expect<
    Mirrors<WorkflowInstanceRelationType, $Enums.WorkflowInstanceRelation>
  >,
];
